import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, AlertTriangle, ExternalLink } from 'lucide-react';
import { CONTACT_INFO, EMERGENCY_CONTACT, DOCUMENTS, IMAGES } from '../data';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-blue-950 text-slate-300">
      {/* Emergency & Quick Info Bar */}
      <div className="border-b border-slate-200 bg-white py-6">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex shrink-0 items-center justify-center text-amber-600">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-900">Ved driftsforstyrrelser (Akut)</h4>
                <p className="text-xs text-slate-600">
                  Kontakt {EMERGENCY_CONTACT.company} på tlf.{' '}
                  <a href={`tel:${EMERGENCY_CONTACT.phone}`} className="font-bold text-blue-600 hover:underline">
                    {EMERGENCY_CONTACT.phone}
                  </a>
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href={DOCUMENTS.takstblad.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3.5 py-2 text-xs font-semibold text-slate-800 transition hover:bg-slate-200 border border-slate-200"
              >
                Hent Takstblad 2026
                <ExternalLink className="h-3 w-3 text-slate-500" />
              </a>
              <a
                href={DOCUMENTS.regulativ.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3.5 py-2 text-xs font-semibold text-slate-800 transition hover:bg-slate-200 border border-slate-200"
              >
                Se Regulativ
                <ExternalLink className="h-3 w-3 text-slate-500" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand Column */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="text-lg font-bold tracking-tight text-white">
                Grindsted Vandværk
              </span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">
              Vi forsyner Grindsted og omegn med rent, velsmagende og kontrolleret drikkevand direkte fra vores lokale boringer.
            </p>
            <div className="pt-2 text-xs text-slate-500">
              Cvr-nr: 20436915
            </div>
          </div>

          {/* Contact Column */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-white">Kontakt</h3>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 shrink-0 text-white" />
                <span>
                  {CONTACT_INFO.address}<br />
                  {CONTACT_INFO.zipCity}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 shrink-0 text-white" />
                <a href={`tel:${CONTACT_INFO.phone}`} className="hover:text-white transition">
                  {CONTACT_INFO.phone} (Formand)
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 shrink-0 text-white" />
                <a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-white transition break-all">
                  {CONTACT_INFO.email}
                </a>
              </li>
            </ul>
          </div>

          {/* Opening Hours Column */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-white">Kontortid</h3>
            <p className="mt-4 text-xs text-slate-400 leading-relaxed">
              Ring til os på tlf. <a href={`tel:${CONTACT_INFO.phoneOffice}`} className="font-semibold text-white hover:underline">{CONTACT_INFO.phoneOffice}</a> i kontortiden:
            </p>
            <ul className="mt-3 space-y-1.5 text-sm">
              {CONTACT_INFO.openingHours.map((item) => (
                <li key={item.day} className="flex justify-between border-b border-white/20 pb-1">
                  <span className="text-slate-400">{item.day}</span>
                  <span className="font-medium text-slate-200">{item.hours}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links Column */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-white">Hurtige links</h3>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <Link to="/" className="hover:text-white transition">Forside</Link>
              </li>
              <li>
                <Link to="/meddelelser" className="hover:text-white transition">Meddelelser & Nyheder</Link>
              </li>
              <li>
                <Link to="/information" className="hover:text-white transition">Information & Vandkvalitet</Link>
              </li>
              <li>
                <Link to="/moedereferater" className="hover:text-white transition">Mødereferater & Årsrapporter</Link>
              </li>
              <li>
                <Link to="/meddel-flytning" className="hover:text-white transition">Meddel Flytning (Flyttekort)</Link>
              </li>
              <li>
                <Link to="/kontakt" className="hover:text-white transition">Bestyrelse & Kontakt</Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 border-t border-white/20 pt-8 text-center text-xs text-slate-500">
          <p>© {currentYear} Grindsted Vandværk. Alle rettigheder forbeholdes.</p>
          <p className="mt-1">Udviklet i overensstemmelse med gældende regulativer og standarder.</p>
        </div>
      </div>
    </footer>
  );
}
